// The chat's pure rules (D82): the encrypted prompt's envelope, what a decrypted event says,
// the history a self-managed device hands the agent, which turn an event belongs to, and which
// queued prompt goes out next, to the segment its turn's worker opened.
import type { ConversationState, EventView } from "../conversation/model";
import type { Turn } from "./api";

export const PROMPT_TYPE = "chat.prompt";
export const HISTORY_LIMIT = 20;

export interface HistoryItem {
  role: "user" | "assistant";
  text: string;
}

/** The plaintext the device encrypts for the turn's worker (`ocs-capture` reads it). */
export function promptEnvelope(turnId: string, text: string, history?: HistoryItem[]): string {
  return JSON.stringify(history && history.length > 0 ? { type: PROMPT_TYPE, turn_id: turnId, text, history } : { type: PROMPT_TYPE, turn_id: turnId, text });
}

/** A `chat.prompt` envelope's turn and text; undefined for anything else. */
export function parsePrompt(plaintext: string): { turnId: string; text: string } | undefined {
  try {
    const v: unknown = JSON.parse(plaintext);
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      if (o.type === PROMPT_TYPE && typeof o.turn_id === "string" && typeof o.text === "string") return { turnId: o.turn_id, text: o.text };
    }
  } catch {
    // a plain instruction
  }
  return undefined;
}

const plain = (e: EventView): string | undefined => (e.content.kind === "plaintext" ? e.content.text : undefined);

/** What the user said in an inbound event: a prompt's text, or an instruction as written. */
export function userText(e: EventView): string | undefined {
  if (e.direction !== "inbound") return undefined;
  const text = plain(e);
  if (text === undefined) return undefined;
  if (e.type === "instruction") return parsePrompt(text)?.text ?? text;
  return undefined;
}

/** The agent's reply in an outbound event: a turn's `result`, not its working output. */
export function agentText(e: EventView): string | undefined {
  if (e.direction !== "outbound" || e.type !== "result") return undefined;
  return plain(e);
}

/** What the agent does on the way to its reply, folded under "Working…". */
const WORKING = new Set<string>(["progress", "assistant", "tool_use", "tool_result"]);
export const isWorking = (e: EventView): boolean => e.direction === "outbound" && WORKING.has(e.type);

/** The worker's echo of a turn's prompt: proof it reached the agent ("delivered"). */
export const isDeliveryEcho = (e: EventView): boolean => e.direction === "outbound" && e.type === "prompt";

/** The last `limit` user and agent messages this device could read, oldest first. */
export function historyPack(events: EventView[], limit = HISTORY_LIMIT): HistoryItem[] {
  const items: HistoryItem[] = [];
  for (const e of events) {
    const u = userText(e);
    if (u !== undefined) items.push({ role: "user", text: u });
    const a = agentText(e);
    if (a !== undefined) items.push({ role: "assistant", text: a });
  }
  return items.slice(-limit);
}

function metaTurn(e: EventView): string | undefined {
  const meta = e.meta;
  if (meta && typeof meta === "object") {
    const id = (meta as Record<string, unknown>).turn_id;
    if (typeof id === "string") return id;
  }
  return undefined;
}

/** event → turn: the event's own `turn_id` metadata, else the turn whose segment holds it. */
export function turnOfEvents(events: EventView[], turns: Turn[] = []): (e: EventView) => string | undefined {
  const bySegment = new Map<string, string>();
  for (const t of turns) if (t.segment_id) bySegment.set(t.segment_id, t.id);
  for (const e of events) {
    const text = e.direction === "inbound" && e.type === "instruction" ? plain(e) : undefined;
    const prompt = text === undefined ? undefined : parsePrompt(text);
    if (prompt && !bySegment.has(e.segmentId)) bySegment.set(e.segmentId, prompt.turnId);
  }
  return (e) => metaTurn(e) ?? bySegment.get(e.segmentId);
}

export interface TurnEntry {
  turn: Turn;
  prompt?: EventView;
  /** The worker's echo of the prompt: delivered to the agent. */
  echo?: EventView;
  progress: EventView[];
  replies: EventView[];
}

export interface Timeline {
  /** Events of no turn (a task's workflow steps), as received. */
  before: EventView[];
  turns: TurnEntry[];
}

export function timeline(events: EventView[], turns: Turn[]): Timeline {
  const turnOf = turnOfEvents(events, turns);
  const entries = new Map(
    [...turns].sort((a, b) => a.created_at - b.created_at).map((t) => [t.id, { turn: t, progress: [], replies: [] } as TurnEntry]),
  );
  const before: EventView[] = [];
  for (const e of events) {
    const id = turnOf(e);
    const entry = id === undefined ? undefined : entries.get(id);
    if (!entry) {
      before.push(e);
      continue;
    }
    if (userText(e) !== undefined && !entry.prompt) entry.prompt = e;
    else if (isDeliveryEcho(e)) entry.echo ??= e;
    else if (isWorking(e)) entry.progress.push(e);
    else entry.replies.push(e);
  }
  return { before, turns: [...entries.values()] };
}

/** A prompt this device has queued, before and after its turn exists. */
export interface PendingPrompt {
  localId: string;
  text: string;
  turnId?: string;
  state: "creating" | "waiting" | "sending" | "sent" | "delivered" | "failed";
  error?: string;
}

/**
 * The prompt to send now, if any, and where: the first one waiting whose turn the server has
 * given a segment (`segment_id`, opened by the turn's worker) that this device can write to.
 */
export function nextToSend(pending: PendingPrompt[], turns: Turn[], state: ConversationState): { prompt: PendingPrompt; segmentId: string } | undefined {
  for (const prompt of pending) {
    if (prompt.state !== "waiting" || !prompt.turnId) continue;
    const segmentId = turns.find((t) => t.id === prompt.turnId)?.segment_id;
    const segment = segmentId ? state.segments.get(segmentId) : undefined;
    if (segment && segment.key.kind === "unwrapped" && segment.closedWith === undefined) return { prompt, segmentId: segment.id };
  }
  return undefined;
}
